/**
 * AdminHelpNotes - FAQ e avisos que a IA do chat de dúvidas usa: lista (ativas e inativas),
 * ativar/desativar e criar aviso com validade opcional.
 */

import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import AdminHelpLoadError from '@/components/admin/help/AdminHelpLoadError';
import { createHelpNote, helpErrorMessage, listHelpNotes, updateHelpNote, type HelpNote } from '@/services/adminHelpService';

const MAX_TEXT = 2000;

const formatDate = (iso: string) => new Date(iso).toLocaleDateString('pt-BR');

/** "aaaa-mm-dd" do input de data → fim daquele dia no horário local, em ISO. */
function endOfDayIso(value: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 23, 59, 59, 999).toISOString();
}

const AdminHelpNotes: React.FC = () => {
  const [notes, setNotes] = useState<HelpNote[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  useEffect(() => {
    let stale = false;
    setLoading(true);
    setError(null);
    listHelpNotes()
      .then((data) => {
        if (!stale) setNotes(data.notes);
      })
      .catch((err) => {
        if (!stale) setError(helpErrorMessage(err));
      })
      .finally(() => {
        if (!stale) setLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [reloadKey]);

  const toggle = async (note: HelpNote) => {
    if (busyId) return;
    setBusyId(note.id);
    setToggleError(null);
    try {
      const { note: saved } = await updateHelpNote(note.id, { active: !note.active });
      setNotes((prev) => (prev ? prev.map((n) => (n.id === saved.id ? saved : n)) : prev));
    } catch (err) {
      setToggleError(helpErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const addNote = (note: HelpNote) => setNotes((prev) => (prev ? [note, ...prev] : [note]));

  return (
    <div className="space-y-6">
      <NewNoticeForm onCreated={addNote} />

      {toggleError && <p className="break-words text-sm text-destructive">{toggleError}</p>}

      {error ? (
        <AdminHelpLoadError message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : loading || !notes ? (
        <div className="py-8 text-center text-muted-foreground">Carregando FAQ e avisos...</div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <NoteSection
            title="FAQ"
            empty="Nenhuma FAQ ainda. Use “Promover a FAQ” numa resposta da equipe."
            notes={notes.filter((n) => n.kind === 'faq')}
            busyId={busyId}
            onToggle={toggle}
          />
          <NoteSection
            title="Avisos"
            empty="Nenhum aviso ainda."
            notes={notes.filter((n) => n.kind === 'notice')}
            busyId={busyId}
            onToggle={toggle}
          />
        </div>
      )}
    </div>
  );
};

function NewNoticeForm({ onCreated }: { onCreated: (note: HelpNote) => void }) {
  const [answer, setAnswer] = useState('');
  const [until, setUntil] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = answer.trim();
    if (!text || saving) return;
    setSaving(true);
    setError(null);
    try {
      const { note } = await createHelpNote({ kind: 'notice', question: null, answer: text, expiresAt: endOfDayIso(until) });
      onCreated(note);
      setAnswer('');
      setUntil('');
    } catch (err) {
      setError(helpErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border border-border p-4">
      <h3 className="text-sm font-semibold text-foreground">Novo aviso</h3>
      <label className="block space-y-1">
        <span className="text-xs font-medium text-muted-foreground">Texto do aviso</span>
        <textarea
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          maxLength={MAX_TEXT}
          rows={3}
          placeholder="Ex.: a exportação em PDF está instável hoje; a equipe já está corrigindo."
          className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
        />
        <span className="block text-right text-xs text-muted-foreground">
          {answer.length}/{MAX_TEXT}
        </span>
      </label>
      <div className="flex flex-wrap items-end gap-3">
        <label className="space-y-1">
          <span className="block text-xs font-medium text-muted-foreground">Vale até (opcional)</span>
          <input
            type="date"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground"
          />
        </label>
        <button
          type="submit"
          disabled={!answer.trim() || saving}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? 'Criando...' : 'Criar aviso'}
        </button>
      </div>
      {error && <p className="break-words text-sm text-destructive">{error}</p>}
    </form>
  );
}

function NoteSection({
  title,
  empty,
  notes,
  busyId,
  onToggle,
}: {
  title: string;
  empty: string;
  notes: HelpNote[];
  busyId: string | null;
  onToggle: (note: HelpNote) => void;
}) {
  return (
    <section className="min-w-0 space-y-3">
      <h3 className="text-sm font-semibold text-foreground">
        {title} <span className="font-normal text-muted-foreground">({notes.length})</span>
      </h3>
      {notes.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">{empty}</div>
      ) : (
        <ul className="space-y-3">
          {notes.map((note) => (
            <li key={note.id} className={cn('space-y-2 rounded-lg border border-border p-3', !note.active && 'bg-muted/30')}>
              {note.question && <p className="whitespace-pre-wrap break-words text-sm font-medium text-foreground">{note.question}</p>}
              <p
                className={cn(
                  'whitespace-pre-wrap break-words text-sm',
                  note.question ? 'text-muted-foreground' : 'text-foreground'
                )}
              >
                {note.answer}
              </p>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">
                  {note.kind === 'faq' ? 'Criada' : 'Criado'} em {formatDate(note.created_at)}
                  {note.expires_at && ` · vale até ${formatDate(note.expires_at)}`}
                </span>
                <button
                  type="button"
                  onClick={() => onToggle(note)}
                  disabled={busyId !== null}
                  aria-pressed={note.active}
                  title={note.active ? 'Clique para desativar' : 'Clique para ativar'}
                  className={cn(
                    'rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                    note.active
                      ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                      : 'border-border bg-muted text-muted-foreground'
                  )}
                >
                  {note.active ? 'Ativa' : 'Inativa'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default AdminHelpNotes;
